export enum CsvErrorCategory {
  SORT = 'sort',
  FILTER = 'filter',
  LAYOUT = 'layout',
  FIELD = 'field',
  VIEW = 'view',
  VALUE = 'value',
  ENTRY = 'entry',
  METADATA = 'metadata',
  PARSING = 'parsing',
}

export enum CsvErrorType {
  PARSING = 'parsing',
  VALIDATION = 'validation',
}

export interface CsvError {
  message: string;
  category: CsvErrorCategory;
  type: CsvErrorType;
  timestamp: number;
  context?: string;
  severity?: 'error' | 'warning';
}

export interface CsvErrorInput {
  message: string;
  category: CsvErrorCategory;
  type: CsvErrorType;
  timestamp: number;
  context?: string;
  severity?: 'error' | 'warning';
}

/**
 * Projects raw collected error data into the public error shape for a caller.
 * Browser AIFC uses the default projection; backend code can opt into extra
 * metadata such as severity.
 */
export type CsvErrorResolver = (error: CsvErrorInput) => CsvError;

/**
 * Preserves the historical AIFC error shape by omitting severity.
 */
export const defaultCsvErrorResolver: CsvErrorResolver = ({
  message,
  category,
  type,
  timestamp,
  context,
}) => ({
  message,
  category,
  type,
  timestamp,
  context,
});

/**
 * Backend projection used when warning/error severity is needed before mapping
 * collected errors into HTTP validation responses.
 */
export const csvErrorWithSeverityResolver: CsvErrorResolver = (error) => ({ ...error });

export class CsvErrorCollector {
  private errors: CsvError[] = [];

  /**
   * Defaults to the browser/AIFC-compatible error shape. Inject a resolver only
   * when the caller needs a different projection of the same collected error.
   */
  constructor(private readonly errorResolver = defaultCsvErrorResolver) {}

  addError(
    message: string,
    category: CsvErrorCategory,
    type: CsvErrorType,
    context?: string,
    severity?: 'error' | 'warning'
  ): void {
    const error = this.errorResolver({
      message,
      category,
      type,
      timestamp: Date.now(),
      context,
      severity,
    });

    this.errors.push(error);
  }

  getErrors(): CsvError[] {
    return [...this.errors];
  }

  clearErrors(): void {
    this.errors = [];
  }

  hasErrors(): boolean {
    return this.errors.length > 0;
  }

  getErrorCount(): number {
    return this.errors.length;
  }

  getErrorsByCategory(category: CsvErrorCategory): CsvError[] {
    return this.errors.filter((error) => error.category === category);
  }

  getErrorsByType(type: CsvErrorType): CsvError[] {
    return this.errors.filter((error) => error.type === type);
  }
}
